import { linkifyHtml } from '../utils/linkify';

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { generateAuditReport, getIndustriesList } from '../services/audit/auditService';
import { exportAuditToPdf } from '../services/audit/pdfAuditService'; 
import type { AuditReport, CategoryScore } from '../types/audit';
import { AWIN_AFFILIATE_URL_PREFIX, NAMECHEAP_SEARCH_URL_PREFIX } from '../constants';
import { RadarChart } from '../components/audit/RadarChart';
import { PdfIcon } from '../components/icons/PdfIcon';
import { SpinnerIcon } from '../components/icons/SpinnerIcon';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSeo } from '../hooks/useSeo';
import { defaultPageContent } from '../contexts/DefaultPageContent';
import { AdminControls } from '../components/AdminControls';
import { useToast } from '../hooks/useToast';
import DOMPurify from 'dompurify';

const LockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-indigo-400">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
  </svg>
);

const ScoreCard: React.FC<{ title: string; data: CategoryScore }> = ({ title, data }) => (
  <div className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex justify-between items-center mb-3">
      <h3 className="font-bold text-gray-800 text-lg">{title}</h3>
      <div className={`px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2 ${
        data.riskLevel === 'Low' ? 'bg-green-100 text-green-800' : 
        data.riskLevel === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
      }`}>
        <span>{data.grade} ({data.score})</span>
        <span className="text-xs uppercase tracking-wide opacity-80">| {data.riskLevel} Risk</span>
      </div>
    </div>
    
    <div className="space-y-4">
        {/* Issues & Strengths */}
        <div className="min-h-[60px]">
            {data.issues.length > 0 ? (
                <ul className="space-y-1">
                {data.issues.map((s, i) => (
                    <li key={`iss-${i}`} className="text-sm text-red-600 flex items-start gap-2">
                    <span className="mt-1 text-xs">⚠</span> {s}
                    </li>
                ))}
                </ul>
            ) : (
                <ul className="space-y-1">
                {data.strengths.slice(0, 2).map((s, i) => (
                    <li key={`str-${i}`} className="text-sm text-green-700 flex items-start gap-2">
                    <span className="mt-1 text-xs">✓</span> {s}
                    </li>
                ))}
                </ul>
            )}
        </div>

        {/* Consultant Layer */}
        <div className="pt-3 border-t border-gray-100 space-y-3">
            <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Why This Matters</p>
                <p className="text-xs text-gray-600 leading-relaxed">{data.whyItMatters}</p>
            </div>
            <div>
                <p className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-1">Consultant Guidance</p>
                <p className="text-xs text-indigo-900 bg-indigo-50 p-2 rounded leading-relaxed italic">"{data.guidance}"</p>
            </div>
        </div>
    </div>
  </div>
);

export const BrandAuditPage: React.FC = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // SEO Hooks
  const { content: allContent, isLoaded, isAdminMode, updatePageContent } = useContent();
  const { showToast } = useToast();
  
  // Fallback to default if content isn't loaded yet or key missing
  const [pageContent, setPageContent] = useState(allContent.brandAudit || defaultPageContent.brandAudit);
  const isLoadingContent = !isLoaded;

  useEffect(() => {
    if (isLoaded && allContent.brandAudit) {
      setPageContent(allContent.brandAudit);
    }
  }, [isLoaded, allContent.brandAudit]);

  const handleSave = async () => {
    if (pageContent) {
      const oldSlug = allContent.brandAudit?.seo?.slug;
      const redirectCreated = await updatePageContent('brandAudit', pageContent);
      showToast('Brand Audit content saved!');
      if (redirectCreated) {
        const newSlug = pageContent.seo.slug;
        showToast(`Redirect created: ${oldSlug} -> ${newSlug}`);
      }
    }
  };
  
  const onSeoApplied = useCallback(() => {
    window.dispatchEvent(new Event('app-rendered'));
  }, []);
  
  useSeo(pageContent, 'brandAudit', onSeoApplied);
  
  const [inputs, setInputs] = useState({
    brandName: '',
    industry: 'fashion',
  });
  const [report, setReport] = useState<AuditReport | null>(null);
  const [loading, setLoading] = useState(false);

  const industries = getIndustriesList();

  // Check for pre-filled brand name from URL
  useEffect(() => {
    const brandParam = searchParams.get('brand');
    if (brandParam) {
        setInputs(prev => ({ ...prev, brandName: brandParam }));
    }
  }, [searchParams]);

  const handleRunAudit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await generateAuditReport(inputs.brandName, inputs.industry, user?.uid);
      setReport(result);
    } catch (err) {
      console.error(err);
      alert("Error generating audit");
    } finally {
      setLoading(false);
    }
  };

  const createAffiliateUrl = (brandName: string) => {
    const namecheapUrl = `${NAMECHEAP_SEARCH_URL_PREFIX}${brandName}`;
    return `${AWIN_AFFILIATE_URL_PREFIX}${encodeURIComponent(namecheapUrl)}`;
  };

  const handleDownloadPdf = () => {
    if (report) {
      exportAuditToPdf(report, user?.customLogoBase64);
    }
  };

  if (isAuthLoading) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="flex items-center text-indigo-600">
                <SpinnerIcon />
                <span className="ml-3 text-lg font-medium">Loading Audit Tools...</span>
            </div>
        </div>
    );
  }

  const isPro = user?.isPro;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 font-sans">
      <div className="max-w-6xl mx-auto">
        <header className="mb-10 flex flex-col md:flex-row items-center justify-between gap-6 border-b border-gray-200 pb-6">
            <a href="https://brandgenius.app" target="_blank" rel="noopener noreferrer" className="transition-opacity hover:opacity-80">
                <img 
                    src="/brand-name-generator-logo.png" 
                    alt="BrandGenius Logo" 
                    className="h-10 w-auto md:h-12" 
                />
            </a>
            <div className="text-center md:text-right">
                <h1 className="text-2xl md:text-3xl font-extrabold text-[#4F46E5]">{pageContent?.h1 || 'Consultant Audit Engine'}</h1>
                {pageContent?.body ? (
                  <div
                      className="text-sm text-gray-500 mt-2 prose prose-sm max-w-none mx-auto md:mx-0 md:ml-auto"
                      dangerouslySetInnerHTML={{ __html: linkifyHtml(pageContent.body) }}
                  />
                ) : (
                  <p className="text-sm text-gray-500">Professional Grade Scoring & Analysis</p>
                )}
            </div>
        </header>

        {isAdminMode && (
            <AdminControls 
                pageId="brandAudit" 
                pageContent={pageContent} 
                onContentChange={setPageContent}
                onSave={handleSave}
            />
        )}

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* LEFT COLUMN: Input */}
            <div className="lg:col-span-3 space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 sticky top-6">
                <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">New Analysis</h2>
                <form onSubmit={handleRunAudit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Brand Name</label>
                    <input
                      type="text"
                      required
                      value={inputs.brandName}
                      onChange={e => setInputs({...inputs, brandName: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all bg-gray-50 text-gray-900"
                      placeholder="e.g. Stripe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Industry</label>
                    <select
                      value={inputs.industry}
                      onChange={e => setInputs({...inputs, industry: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-gray-50 text-gray-900 transition-all"
                    >
                      {industries.map(ind => (
                        <option key={ind} value={ind} className="bg-white">{ind.charAt(0).toUpperCase() + ind.slice(1).replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                  </div>
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white py-3.5 rounded-xl font-bold transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-70 disabled:transform-none disabled:shadow-none hover:-translate-y-0.5"
                    >
                      {loading ? 'Running Analysis...' : 'Generate Report'}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* RIGHT COLUMN: Results */}
            <div className="lg:col-span-9">
              {report ? (
                <div className="space-y-8 animate-fade-in">
                  
                  {/* 1. Executive Summary & Radar */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="p-8 flex flex-col md:flex-row gap-8">
                      
                      {/* Score Circle */}
                      <div className="flex-shrink-0 flex flex-col items-center justify-center md:border-r md:border-gray-100 md:pr-8">
                        <div className={`w-40 h-40 rounded-full flex items-center justify-center border-[6px] ${
                          report.overallScore >= 80 ? 'border-green-500 text-green-600' : 
                          report.overallScore >= 60 ? 'border-yellow-500 text-yellow-600' : 'border-red-500 text-red-600'
                        }`}>
                          <div className="text-center">
                            <span className="block text-6xl font-extrabold tracking-tighter">
                              {report.grade}
                            </span>
                            <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">Grade</span>
                          </div>
                        </div>
                        <div className="mt-4 text-center">
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-bold uppercase tracking-wide">
                                {report.brandType} Brand
                            </span>
                        </div>
                      </div>

                      {/* Summary Text & Radar */}
                      <div className="flex-grow flex flex-col gap-6">
                          <div>
                              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">Executive Summary</h3>
                              <p className="text-gray-700 leading-relaxed text-lg">
                                  {report.consultantSummary}
                              </p>
                          </div>
                          <div className="h-px bg-gray-100 w-full"></div>
                          <div className="flex items-center justify-center h-48">
                              <RadarChart 
                                  size={250}
                                  data={[
                                  { label: 'Linguistics', value: report.categories.linguistics.score },
                                  { label: 'Phonetics', value: report.categories.phonetics.score },
                                  { label: 'Digital', value: report.categories.digital.score },
                                  { label: 'Structure', value: report.categories.structural.score },
                                  { label: 'Strategy', value: report.categories.strategic.score },
                                  ]}
                              />
                          </div>
                      </div>
                    </div>
                  </div>

                  {/* Blurred section for non-pro */}
                  <div className={`relative ${!isPro ? 'select-none' : ''}`}>
                    <div className={`${!isPro ? 'blur-[8px] pointer-events-none opacity-50' : ''} space-y-8`}>
                      {/* Custom Consultant Notes */}
                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-8">
                        <h3 className="text-lg font-bold text-gray-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                           <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                           Custom Consultant Notes
                        </h3>
                        {isPro ? (
                           <textarea
                              className="w-full h-32 p-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-y"
                              placeholder="Add your own notes, analysis, or recommendations here. These will be included in the PDF export."
                              value={report.consultantNotes || ''}
                              onChange={(e) => setReport({ ...report, consultantNotes: e.target.value })}
                           />
                        ) : (
                           <div className="w-full h-32 p-4 border border-gray-200 bg-gray-50 rounded-md text-gray-400 flex items-center justify-center">
                             Upgrade to Pro to add custom consultant notes to your reports.
                           </div>
                        )}
                      </div>

                      {/* 2. Consultant Action Plan */}
                      <div className="bg-indigo-900 rounded-xl shadow-lg text-white p-8 overflow-hidden relative">
                      <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white opacity-10 rounded-full blur-xl"></div>
                      <h3 className="text-lg font-bold text-indigo-100 uppercase tracking-wider mb-4 flex items-center gap-2">
                          <span className="bg-indigo-500 w-6 h-6 rounded-full flex items-center justify-center text-xs">!</span> 
                          Recommended Next Actions
                      </h3>
                      <ul className="space-y-3 relative z-10">
                          {report.feedback.actions.map((action, i) => (
                          <li key={i} className="flex items-start gap-3 text-indigo-50 text-sm md:text-base">
                              <span className="text-indigo-400 mt-1 font-bold">{i + 1}.</span>
                              <span>{action}</span>
                          </li>
                          ))}
                      </ul>
                  </div>

                  {/* 3. Detailed Breakdown Grid */}
                  <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-4 ml-1">Detailed Category Analysis</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <ScoreCard title="Linguistic Efficiency" data={report.categories.linguistics} />
                          <ScoreCard title="Phonetic Clarity" data={report.categories.phonetics} />
                          <ScoreCard title="Digital Readiness" data={report.categories.digital} />
                          <ScoreCard title="Structural Integrity" data={report.categories.structural} />
                          <ScoreCard title="Strategic Intent" data={report.categories.strategic} />
                      </div>
                  </div>

                  {isPro && (
                    <div className="text-center pt-8 pb-4 flex flex-col md:flex-row justify-center gap-4">
                      <a 
                        href={createAffiliateUrl(report.inputs.name)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#10B981] hover:bg-[#34D399] text-white font-bold rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                      >
                        Check {report.inputs.name}.com Availability
                      </a>
                      
                      <button
                        onClick={handleDownloadPdf}
                        className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                      >
                        <PdfIcon />
                        Download Consultant Report (PDF)
                      </button>
                    </div>
                  )}
                </div>

                {!isPro && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center z-10 p-6">
                      <div className="bg-white/90 backdrop-blur-md p-8 rounded-xl shadow-2xl border border-indigo-200 text-center max-w-md">
                          <div className="mx-auto w-12 h-12 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mb-4">
                            <LockIcon />
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-3">Unlock Full Consultant Report</h3>
                          <p className="text-gray-600 mb-6">Upgrade to BrandGenius Pro to access the detailed category analysis, action plan, and domain availability checks.</p>
                          <button 
                            onClick={() => navigate('/pricing/')}
                            className="w-full px-6 py-3 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold rounded-lg shadow-md transition-all duration-300"
                          >
                            Upgrade to Pro
                          </button>
                      </div>
                  </div>
                )}
              </div>

              {/* Loop Back CTA */}
              <div className="mt-8 p-8 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 text-center">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Want to explore more brand name ideas?</h3>
                  <button
                      onClick={() => navigate('/')}
                      className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-bold hover:underline transition-all text-lg"
                  >
                      Go to our Brand Name Generator →
                  </button>
              </div>

                </div>
              ) : (
                <div className="h-96 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50/50">
                  <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className="text-lg font-medium text-gray-500">Ready to Analyze</p>
                  <p className="text-sm">Enter a brand name to generate a consultant-grade report.</p>
                </div>
              )}
            </div>
          </div>

        {pageContent?.bodyBelow && (
          <div
            className="text-[#6B7280] mt-12 prose max-w-none mx-auto bg-white p-8 rounded-xl shadow-sm border border-gray-200"
            dangerouslySetInnerHTML={{ __html: linkifyHtml(pageContent.bodyBelow) }}
          />
        )}
      </div>
    </div>
  );
};
